import React, { useState, useEffect } from 'react';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Card from '../../ui/Card';
import { initializeTournamentLeaderboard } from '../adminApiService';
import toast from 'react-hot-toast';

const TournamentDetailsTab = ({ 
  tournament, 
  canModify, 
  updateMutation, 
  onChanges 
}) => {
  const [formData, setFormData] = useState({
    name: tournament?.name || '',
    description: tournament?.description || '',
    startDate: tournament?.startDate ? new Date(tournament.startDate).toISOString().slice(0, 16) : '',
    endDate: tournament?.endDate ? new Date(tournament.endDate).toISOString().slice(0, 16) : '',
    maxPlayers: tournament?.maxPlayers || '',
    status: tournament?.status || 'DRAFT'
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [isInitializingLeaderboard, setIsInitializingLeaderboard] = useState(false);

  // Update form when tournament prop changes
  useEffect(() => {
    if (tournament) {
      setFormData({
        name: tournament.name || '',
        description: tournament.description || '',
        startDate: tournament.startDate ? new Date(tournament.startDate).toISOString().slice(0, 16) : '',
        endDate: tournament.endDate ? new Date(tournament.endDate).toISOString().slice(0, 16) : '',
        maxPlayers: tournament.maxPlayers || '',
        status: tournament.status || 'DRAFT'
      });
      setHasChanges(false);
    }
  }, [tournament]);

  // Notify parent of changes
  useEffect(() => {
    onChanges(hasChanges);
  }, [hasChanges, onChanges]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({
        id: tournament.id,
        ...formData,
        maxPlayers: parseInt(formData.maxPlayers) || 0
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving tournament:', error);
    }
  };

  const handleReset = () => {
    setFormData({
      name: tournament.name || '',
      description: tournament.description || '',
      startDate: tournament.startDate ? new Date(tournament.startDate).toISOString().slice(0, 16) : '',
      endDate: tournament.endDate ? new Date(tournament.endDate).toISOString().slice(0, 16) : '',
      maxPlayers: tournament.maxPlayers || '',
      status: tournament.status || 'DRAFT'
    });
    setHasChanges(false);
  };

  const handleInitializeLeaderboard = async () => {
    if (window.confirm('Are you sure you want to initialize the leaderboard for this tournament?\n\nThis will create an initial leaderboard with all players showing 0 stats, ready for live betting.')) {
      try {
        setIsInitializingLeaderboard(true);
        await initializeTournamentLeaderboard(tournament.id);
        toast.success('Leaderboard initialized successfully!');
      } catch (error) {
        console.error('Error initializing leaderboard:', error);
        toast.error(error.message || 'Failed to initialize leaderboard');
      } finally {
        setIsInitializingLeaderboard(false);
      }
    }
  };


  const statusOptions = [
    { value: 'DRAFT', label: 'Draft', description: 'Tournament is being set up' },
    { value: 'ACTIVE', label: 'Active', description: 'Tournament is active and accepting bets' },
    { value: 'LIVE', label: 'Live', description: 'Tournament is live with real-time updates' },
    { value: 'COMPLETED', label: 'Completed', description: 'Tournament has finished' },
    { value: 'CANCELLED', label: 'Cancelled', description: 'Tournament was cancelled' }
  ];

  return (
    <div className="admin-tournament-details-tab">
      <div className="tab-header">
        <div>
          <h2>Tournament Details</h2>
          <p>Manage basic tournament information and settings</p>
        </div>
      </div>

      <div className="details-content">
        <div className="details-grid">
          {/* Basic Information */}
          <Card className="details-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label htmlFor="name">Tournament Name *</label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Enter tournament name"
                disabled={!canModify}
                lightTheme={true}
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter tournament description"
                disabled={!canModify}
                rows={4}
                className="form-textarea"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startDate">Start Date *</label>
                <Input
                  id="startDate"
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  disabled={!canModify}
                  lang="en-US"
                  lightTheme={true}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endDate">End Date *</label>
                <Input
                  id="endDate"
                  type="datetime-local"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  disabled={!canModify}
                  lang="en-US"
                  lightTheme={true}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="maxPlayers">Maximum Players</label>
              <Input
                id="maxPlayers"
                type="number"
                value={formData.maxPlayers}
                onChange={(e) => handleInputChange('maxPlayers', e.target.value)}
                placeholder="Enter maximum number of players"
                disabled={!canModify}
                min="1"
                lightTheme={true}
              />
            </div>
          </Card>


          {/* Status Management */}
          <Card className="details-section">
            <h3>Tournament Status</h3>
            
            <div className="form-group">
              <label htmlFor="status">Current Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                disabled={!canModify || tournament.status === 'COMPLETED'}
                className="form-select"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <small className="form-help">
                {statusOptions.find(opt => opt.value === formData.status)?.description}
              </small>
            </div>

            {tournament.status !== 'DRAFT' && (
              <div className="status-warning">
                <strong>Note:</strong> Some modifications are restricted once a tournament moves out of DRAFT status.
              </div>
            )}

            {/* Leaderboard Management */}
            {tournament.status === 'ACTIVE' && (
              <div className="leaderboard-section">
                <h4>Leaderboard Management</h4>
                <p>Initialize the leaderboard for live betting. This creates an initial leaderboard with all players showing 0 stats.</p>
                <div className="leaderboard-buttons">
                  <Button
                    onClick={handleInitializeLeaderboard}
                    disabled={isInitializingLeaderboard}
                    variant="primary"
                    className="init-leaderboard-btn"
                  >
                    {isInitializingLeaderboard ? 'Initializing...' : 'Initialize Leaderboard'}
                  </Button>
                  
                </div>
                
              </div>
            )}
          </Card>
        </div>

        {/* Action Buttons */}
        {canModify && (
          <div className="details-actions">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isLoading}
              className="save-btn"
            >
              {updateMutation.isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
            
            <Button
              onClick={handleReset}
              disabled={!hasChanges}
              variant="secondary"
              className="reset-btn"
            >
              Reset Changes
            </Button>
          </div>
        )}

        {!canModify && (
          <div className="readonly-notice">
            <p>This tournament cannot be modified because it is LIVE. Only DRAFT and ACTIVE tournaments can be edited.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentDetailsTab;